import React, { useState, useEffect } from 'react';
import { eventsAPI } from '../services/api';
import './Events.css';

interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  description: string;
  image?: string;
  imageUrl?: string;
  images?: string[];
  isRecurring?: boolean;
  recurringType?: 'NONE' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  recurringDay?: number; // Day of week (0-6) for weekly, day of month (1-31) for monthly
  recurringWeek?: number; // Week of month (1-4, 5 for last) for monthly events
  recurringEndDate?: string; // When to stop recurring

  // Hybrid approach fields
  parentEventId?: string;
  isGenerated?: boolean;
  isCancelled?: boolean;
  isModified?: boolean;
}

// With the hybrid approach, we no longer need complex date calculations
// The backend generates individual event occurrences for us!

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);

  // Date navigation state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);

        // Fetch events for the specific month and year
        // The backend will calculate recurring occurrences in memory
        const response = await eventsAPI.getAllForMonth(selectedYear, selectedMonth);
        const allEvents = response.data || [];

        // Filter events for the selected month and year (in case backend returns extra)
        const filteredEvents = allEvents.filter((event: Event) => {
          const eventDate = new Date(event.date);
          return eventDate.getMonth() === selectedMonth && eventDate.getFullYear() === selectedYear;
        });

        setEvents(filteredEvents);
      } catch (error: any) {
        console.error('Error fetching events:', error);
        setError('Failed to load events. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [selectedMonth, selectedYear]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getMonthName = (monthIndex: number) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };



  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
    setShowEventModal(false);
  };

  return (
    <div className="events-page">
      <section 
        className="events-header"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${process.env.PUBLIC_URL}/Historic-Molly-Butler-Lodge-And-Restaurant-Greer-AZ.jpg')`
        }}
      >
        <div className="container">
          <h1>Upcoming Events</h1>
          <p>Join us for special events and experiences at Arizona's oldest guest lodge</p>
        </div>
      </section>

      <section className="events-content">
        <div className="container">
          {/* Date Navigation Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '2rem',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '12px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  minWidth: '120px'
                }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {getMonthName(i)}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {loading && (
            <div className="no-events">
              <p>Loading events...</p>
            </div>
          )}

          {error && (
            <div className="no-events">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Current view description */}
              <div style={{
                textAlign: 'center',
                marginBottom: '1.5rem',
                color: '#666',
                fontSize: '0.95rem'
              }}>
                Showing events for {getMonthName(selectedMonth)} {selectedYear}
              </div>

              {/* Events List */}
              <div className="events-grid">
                {events.map(event => {
                  const hasImage = (event.images && event.images.length > 0) || event.imageUrl || event.image;
                  return (
                  <div
                    key={event.id}
                    className={`event-card ${!hasImage ? 'no-image' : ''}`}
                    onClick={() => handleEventClick(event)}
                    style={{cursor: 'pointer'}}
                  >
                    {(event.images && event.images.length > 0 ? event.images[0] : (event.imageUrl || event.image)) && (
                      <div className="event-image">
                        <img
                          src={event.images && event.images.length > 0 ? event.images[0] : (event.imageUrl || event.image)}
                          alt={event.title}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {event.images && event.images.length > 1 && (
                          <div className="image-count-badge" style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.8rem'
                          }}>
                            +{event.images.length - 1}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="event-date">
                      <div className="date-display">
                        {new Date(event.date).getDate()}
                      </div>
                      <div className="month-display">
                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      {event.isGenerated && (
                        <div style={{
                          fontSize: '0.6rem',
                          color: 'rgba(255,255,255,0.8)',
                          textAlign: 'center',
                          marginTop: '2px'
                        }}>
                          RECURRING
                        </div>
                      )}
                    </div>
                    <div className="event-content">
                      <h3 className="event-title">{event.title}</h3>
                      <div className="event-datetime">
                        <div className="event-full-date">
                          {formatDate(event.date)}
                          {event.isGenerated && (
                            <span style={{
                              marginLeft: '0.5rem',
                              fontSize: '0.8rem',
                              color: 'var(--lodge-orange)',
                              fontWeight: 'bold'
                            }}>
                              • Recurring series
                            </span>
                          )}
                        </div>
                        <div className="event-time">{event.time}</div>
                      </div>
                      <p className="event-description">{event.description.length > 100 ? event.description.substring(0, 100) + '...' : event.description}</p>
                      <div className="event-click-hint">Click for more details</div>
                    </div>
                  </div>
                );
                })}
              </div>

              {events.length === 0 && (
                <div className="no-events">
                  <p>No upcoming events at this time.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Event Detail Modal */}
      {showEventModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeEventModal}>
          <div className="modal-content event-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedEvent.title}</h2>
              <button className="modal-close" onClick={closeEventModal}>
                ×
              </button>
            </div>
            
            <div className="event-detail-body">
              {/* Event Images Gallery */}
              {(selectedEvent.images && selectedEvent.images.length > 0) ? (
                <div className="event-detail-images">
                  <div className="image-gallery" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '10px',
                    marginBottom: '20px'
                  }}>
                    {selectedEvent.images.map((imageUrl, index) => (
                      <div key={index} className="gallery-image">
                        <img
                          src={imageUrl}
                          alt={`${selectedEvent.title} ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (selectedEvent.imageUrl || selectedEvent.image) && (
                <div className="event-detail-image">
                  <img
                    src={selectedEvent.imageUrl || selectedEvent.image}
                    alt={selectedEvent.title}
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div className="event-detail-info">
                <div className="event-detail-datetime">
                  <div className="event-detail-date">
                    <strong>Date:</strong> {formatDate(selectedEvent.date)}
                  </div>
                  <div className="event-detail-time">
                    <strong>Time:</strong> {selectedEvent.time}
                  </div>
                </div>
                
                <div className="event-detail-description">
                  <h3>About This Event</h3>
                  <p>{selectedEvent.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;